import { FormEvent, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useResetPassword } from "./useResetPassword";
import { AuthShell } from "./AuthShell";
import { Field } from "../ui/Field";
import { TextInput } from "../ui/TextInput";
import { Button } from "../ui/Button";
import { Banner } from "../ui/Banner";
import { pickFieldError } from "../lib/form";
import { useTranslation, apiErrorMessage } from "../i18n";

export function ResetPasswordPage() {
  const { t } = useTranslation("auth");
  const { t: tErr } = useTranslation("errors");
  const { token = "" } = useParams();
  const [newPassword, setNewPassword] = useState("");
  const navigate = useNavigate();
  const reset = useResetPassword();

  function onSubmit(e: FormEvent): void {
    e.preventDefault();
    reset.mutate(
      { token, newPassword },
      { onSuccess: () => void navigate("/sign-in", { replace: true }) },
    );
  }

  const passwordError = pickFieldError(reset.error, "newPassword");
  // A spent/expired/unknown token comes back as a non-field error — surface it
  // with a route back to request a fresh link.
  const genericError =
    reset.error && reset.error.code !== "validation_failed"
      ? apiErrorMessage(reset.error, tErr)
      : reset.error && !passwordError
        ? reset.error.message
        : undefined;

  return (
    <AuthShell>
      <h1 className="mb-6 text-2xl font-semibold text-slate-100">{t("resetTitle")}</h1>

      {genericError ? (
        <Banner kind="error" className="mb-4">
          {genericError}{" "}
          <Link to="/forgot-password" className="text-arch-400 hover:underline">
            {t("resetRequestNew")}
          </Link>
        </Banner>
      ) : null}

      <form onSubmit={onSubmit} className="space-y-4" noValidate>
        <Field label={t("newPassword")} error={passwordError}>
          {({ id, ...ariaProps }) => (
            <TextInput
              id={id}
              type="password"
              autoComplete="new-password"
              required
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              hasError={!!passwordError}
              {...ariaProps}
            />
          )}
        </Field>

        <Button
          type="submit"
          variant="primary"
          className="w-full"
          disabled={reset.isPending}
        >
          {reset.isPending ? t("resetting") : t("resetSubmit")}
        </Button>
      </form>
    </AuthShell>
  );
}
